import { useEffect, useRef, useState } from 'react';

/**
 * Returns [ref, isInView] — element is considered in view when at least
 * `threshold` of its area intersects the viewport. Triggers re-fire
 * each time the element re-enters (so animations can replay on scroll).
 */
export default function useInView({ threshold = 0.2, rootMargin = '0px', once = false } = {}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (typeof IntersectionObserver === 'undefined') {
            // SSR / unsupported: assume visible
            setInView(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (once) observer.unobserve(node);
                } else if (!once) {
                    setInView(false);
                }
            },
            { threshold, rootMargin }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [threshold, rootMargin, once]);

    return [ref, inView];
}
